import React, { useEffect, useState } from 'react';
import './UserList.scss';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  TextField,
  Snackbar,
  FormGroup
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import GroupIcon from '@material-ui/icons/Group';
import VisibilityIcon from '@material-ui/icons/Visibility';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import PeopleIcon from '@material-ui/icons/People';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import { http, FxInput, useNavigationService, FxSelect, localstorage } from '../../fx-core';
import { NoRecords } from '../../NoRecords/NoRecords';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Typography from '@material-ui/core/Typography';
import { mkConfig, generateCsv, download } from "export-to-csv";
import MuiAlert from '@material-ui/lab/Alert';

const UserList = () => {
  const { routeData, back } = useNavigationService();
  let defaultContext: any = {
    firstName: "",
    communityId: routeData.communityId
  };

  // ── Main list state ──
  const [currentContext, setCurrentContext] = useState(defaultContext);
  const [users, setUsers] = useState([]);
  const [communityList, setCommunityList] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;
  const [totalUsers, setTotalUsers] = useState(0);
  const [tagFilter, setTagFilter] = useState<'all' | 'resident' | 'merchant'>('all');

  // ── Create / Edit group modal state ──
  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [groupName, setGroupName] = useState("");
  const [parentCommunityName, setParentCommunityName] = useState("");
  const [parentCommunityId, setParentCommunityId] = useState(null);
  const [allCommunities, setAllCommunities] = useState([]);
  const [selectedCommunities, setSelectedCommunities] = useState<any[]>([]);
  const [selectAllCommunities, setSelectAllCommunities] = useState(false);
  const [pendingSelectedCommunityIds, setPendingSelectedCommunityIds] = useState<string[]>([]);
  const [sciCommunities, setSciCommunities] = useState<any[]>([]);
  const [sciResidentUserIdsByCommunity, setSciResidentUserIdsByCommunity] = useState<Record<number, number[]>>({});
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userSearchText, setUserSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  // ── Groups list state ──
  const [createdGroups, setCreatedGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  // ── View group modal state ──
  const [viewGroupModalOpen, setViewGroupModalOpen] = useState(false);
  const [viewGroupData, setViewGroupData] = useState(null);
  const [loadingGroupCommunities, setLoadingGroupCommunities] = useState(false);

  // ── Manage members modal state ──
  const [manageMembersOpen, setManageMembersOpen] = useState(false);
  const [managingGroup, setManagingGroup] = useState<any>(null);
  const [memberTab, setMemberTab] = useState<'current' | 'add'>('current');
  const [memberSearchText, setMemberSearchText] = useState("");
  const [addSearchText, setAddSearchText] = useState("");
  const [addCommunityFilter, setAddCommunityFilter] = useState("all");
  const [selectedToRemove, setSelectedToRemove] = useState<number[]>([]);
  const [selectedToAdd, setSelectedToAdd] = useState<number[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [newUsersForGroup, setNewUsersForGroup] = useState<any[]>([]);
  const [loadingNewUsers, setLoadingNewUsers] = useState(false);
  const [newUsersChecked, setNewUsersChecked] = useState(false);

  // ── Notification modal state ──
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [selectedGroupForNotification, setSelectedGroupForNotification] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [notificationImageUrl, setNotificationImageUrl] = useState("");
  const [notificationLink, setNotificationLink] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [confirmSendDialogOpen, setConfirmSendDialogOpen] = useState(false);

  // ── Snackbar ──
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────
  const showSnack = (msg: string, type: "success" | "error" = "success") => {
    setSnackbarMsg(msg);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  const toCamelCase = (value?: string) =>
    value ? value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : "";

  const formatPhone = (mobile?: string) => {
    if (!mobile) return "";
    const digits = mobile.replace(/\D/g, "");
    if (digits.length === 10 || (digits.length === 12 && digits.startsWith("91"))) return digits;
    return digits || mobile;
  };

  const formatDate = (date?: string) =>
    date ? new Date(date).toISOString().split("T")[0] : "";

  const formatTime = (dateString: string | null): string => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return ""; }
  };

  const csvConfig = mkConfig({ filename: "community_users", useKeysAsHeaders: true, quoteStrings: true });

  const csvData = users.map((row: any) => {
    const user = row.user || {};
    const address = row.addresses?.[0] || {};
    const orders = row.orderDetails || {};
    return {
      created_date: formatDate(user.createdDate),
      created_time: formatTime(user.createdDate),
      name: [user.firstName, user.lastName].filter(Boolean).join(" "),
      phone: formatPhone(user.mobile),
      email: user.email || "",
      community: toCamelCase(user.communityName),
      Campaign: user.myReferralCode || user.referralCode || "",
      address_line_1: address.fullAddress || "",
      city: toCamelCase(address.city),
      state: toCamelCase(address.state),
      pincode: address.pinCode || "",
      total_order_value: Number(orders.totalOrderValue || 0).toFixed(2),
      total_orders: Number(orders.totalOrders || 0),
      last_order_date: formatDate(orders.lastOrderDate),
      last_active_date: formatDate(user.modifiedDate) || formatDate(user.createdDate)
    };
  });

  // ─────────────────────────────────────────────
  // Main user list
  // ─────────────────────────────────────────────
  const handleInputChange = (item) => {
    handleItemChange({ [item.name]: item.value });
    let filterObj = { firstName: currentContext.firstName, communityId: currentContext.communityId };
    if (item.name === "firstName") filterObj.firstName = item.value;
    else if (item.name === 'communityId') filterObj.communityId = item.value;
    getUsers(filterObj, 1);
  };

  const handleItemChange = (data) => {
    setCurrentContext(prev => ({ ...prev, ...data }));
  };

  const getUsers = (data: any, pageNumber = 1) => {
    setUsersLoading(true);
    let inputData = { defaultCommunityId: data.communityId, firstName: data.firstName, pageNumber, pageSize: rowsPerPage };
    http.post({ url: `user/community/dashboard`, data: inputData }).then(res => {
      const resp = res.data || {};
      const items = resp.items || resp.Items || resp || [];
      const total = resp.totalCount || resp.TotalCount || (Array.isArray(items) ? items.length : 0);
      const list = Array.isArray(items) ? [...items] : [];
      list.sort((a, b) => {
        const ad = a.user?.createdDate || a.createdDate || a.user?.createdOn;
        const bd = b.user?.createdDate || b.createdDate || b.user?.createdOn;
        return new Date(bd).getTime() - new Date(ad).getTime();
      });
      setUsers(list);
      setTotalUsers(total);
      setPage(Math.max(0, pageNumber - 1));
    }).catch(err => {
      console.error("Error fetching paged users:", err);
      setUsers([]);
      setTotalUsers(0);
    }).finally(() => setUsersLoading(false));
  };

  const navToBack = () => back();

  // ─────────────────────────────────────────────
  // Community list for filter dropdown
  // ─────────────────────────────────────────────
  const getMyRegionCommunities = () => {
    http.post({ url: 'options/lookup', data: { lookups: { MyRegionCommunities: { default: false } }, filters: { userId: localstorage.getItem("userId") } } })
      .then(res => setCommunityList(res.data.MyRegionCommunities));
  };

  const getMyZoneCommunities = () => {
    http.post({ url: 'options/lookup', data: { lookups: { MyZoneCommunities: { default: false } }, filters: { userId: localstorage.getItem("userId") } } })
      .then(res => setCommunityList(res.data.MyZoneCommunities));
  };

  const getMyAdminCommunities = () => {
    http.post({ url: 'community/role/list', data: {} }).then(res => {
      setCommunityList(res.data.map((item: any) => ({ id: item.id, text: item.name })));
    });
  };

  const initCalls = () => {
    if (routeData.parent === 'REGIONALMANAGER') getMyRegionCommunities();
    else if (routeData.parent === 'ZONALMANAGER') getMyZoneCommunities();
    else if (routeData.parent === 'COMMADMIN') getMyAdminCommunities();
  };

  // ─────────────────────────────────────────────
  // Fetch all users (for group creation / manage members)
  // ─────────────────────────────────────────────
  const fetchAllUsers = () => {
    setUsersLoading(true);
    http.get({ url: 'users', data: {} }).then(res => {
      setAllUsers(res.data);
    }).catch(err => {
      console.error("Error fetching users:", err);
      showSnack("Failed to load users.", "error");
    }).finally(() => setUsersLoading(false));
  };

  const fetchAllUsersPromise = (): Promise<void> => {
    setUsersLoading(true);
    return http.get({ url: 'users', data: {} })
      .then(res => setAllUsers(res.data))
      .catch(err => console.error("Error fetching users:", err))
      .finally(() => setUsersLoading(false));
  };

  // ─────────────────────────────────────────────
  // Groups
  // ─────────────────────────────────────────────
  const fetchExistingGroups = () => {
    setGroupsLoading(true);
    http.get({ url: 'notificationgroup/groups', data: {} }).then(res => {
      const result = res.data;
      if (result && Array.isArray(result)) {
        const parsed = result.map((group: any) => {
          let userData = [];
          try {
            if (group.userDetails) userData = JSON.parse(group.userDetails);
            else if (group.userIds) {
              const ids = group.userIds.split(',').map(id => parseInt(id.trim()));
              userData = ids.map(id => ({ id, firstName: 'User', email: '' }));
            }
          } catch (e) { console.error("Error parsing user data:", e); }
          return {
            id: group.id,
            name: group.groupName || "Unnamed Group",
            users: userData,
            communityName: group.communityName || "",
            allCommunities: group.communityIds ? group.communityIds.split(',').map(id => id.trim()) : [],
            totalCommunities: group.totalCommunities || 0,
            communityIds: group.communityIds || "",
            createdDate: group.createdDate
          };
        });
        setCreatedGroups(parsed);
      }
    }).catch(err => console.error("Error fetching groups:", err))
      .finally(() => setGroupsLoading(false));
  };

  const fetchCommunityNamesForGroup = async (communityIds: string) => {
    if (!communityIds) return [];
    try {
      const idArray = communityIds.split(',').map(id => id.trim());
      const promises = idArray.map(id =>
        http.get({ url: `community/${id}` })
          .then(res => res.data.name || res.data.Name || res.data.communityName || `Community ${id}`)
          .catch(() => `Community ${id}`)
      );
      return await Promise.all(promises);
    } catch { return []; }
  };

  // ─────────────────────────────────────────────
  // Create / Edit group modal
  // ─────────────────────────────────────────────
  const handleOpenGroupModal = () => {
    setEditingGroup(null);
    setOpenGroupModal(true);
    findAllCommunitiesForGroupCreation();
    fetchSciCommunitiesForGroupCreation();
    fetchAllUsers();
  };

  const handleEditGroup = async (group: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroup(group);
    setGroupName(group.name);
    setOpenGroupModal(true);
    findAllCommunitiesForGroupCreation();
    fetchSciCommunitiesForGroupCreation();
    await fetchAllUsersPromise();
    if (group.communityIds) {
      const ids = group.communityIds.split(',').map((id: string) => id.trim());
      setPendingSelectedCommunityIds(ids);
    }
    if (group.users?.length > 0) {
      setSelectedUsers(group.users.map((u: any) => u.id).filter(Boolean));
    }
  };

  // Apply pending community selections once allCommunities loads
  useEffect(() => {
    if (pendingSelectedCommunityIds.length > 0 && allCommunities.length > 0) {
      const matched = allCommunities.filter((c: any) =>
        pendingSelectedCommunityIds.includes(String(c.Id))
      );
      setSelectedCommunities(matched);
      setSelectAllCommunities(matched.length === allCommunities.length && matched.length > 0);
      setPendingSelectedCommunityIds([]);
    }
  }, [allCommunities, pendingSelectedCommunityIds]);

  const handleCloseGroupModal = () => {
    setOpenGroupModal(false);
    setEditingGroup(null);
    setPendingSelectedCommunityIds([]);
    resetGroupForm();
  };

  const resetGroupForm = () => {
    setGroupName("");
    setSelectedCommunities([]);
    setSelectAllCommunities(false);
    setSelectedUsers([]);
    setFilteredUsers([]);
    setUserSearchText("");
  };

  const findAllCommunitiesForGroupCreation = () => {
    setCommunitiesLoading(true);
    const selectedCommunityId = currentContext.communityId || 20001;
    http.post({ url: 'community/locationwise/list', data: { communityId: 20001 } }).then(res => {
      const result = res.data;
      if (result?.items?.length > 0) {
        let foundParent = null;
        for (let item of result.items) {
          if (item.items?.length > 0) {
            if (item.items.some(child => child.Id === selectedCommunityId) || item.Id === selectedCommunityId) {
              foundParent = item;
              break;
            }
          }
        }
        if (foundParent) {
          setParentCommunityName(foundParent.Name);
          setParentCommunityId(foundParent.Id);
          setAllCommunities(foundParent.items?.length > 0 ? foundParent.items : []);
        } else {
          fetchDirectChildren(selectedCommunityId);
        }
      } else {
        setAllCommunities([]);
        setParentCommunityName("");
      }
    }).catch(err => {
      console.error("Error fetching community hierarchy:", err);
      showSnack("Failed to load communities.", "error");
    }).finally(() => setCommunitiesLoading(false));
  };

  // SCI-linked special communities (e.g. Central Park) don't show up as
  // selectable group targets through the normal community hierarchy alone,
  // because their residents' DefaultCommunityId is the PARENT community, not
  // the special community itself — so a name-match filter can't find them.
  // Kept in their own state (rather than merged into allCommunities) since
  // findAllCommunitiesForGroupCreation's setAllCommunities call overwrites
  // rather than merges, and the two fetches race.
  const fetchSciCommunitiesForGroupCreation = () => {
    http.get({ url: 'community/sci-linked-list' }).then((res: any) => {
      const communities = res.data?.communities || [];
      setSciCommunities(communities.map((c: any) => ({ Id: c.id, Name: c.name, isSci: true })));

      communities.forEach((c: any) => {
        http.get({ url: `community/${c.id}/sci-resident-user-ids` }).then((r: any) => {
          setSciResidentUserIdsByCommunity(prev => ({ ...prev, [c.id]: r.data?.userIds || [] }));
        }).catch(() => {});
      });
    }).catch(() => setSciCommunities([]));
  };

  const communityOptions = [...allCommunities, ...sciCommunities];

  const fetchDirectChildren = (communityId) => {
    http.post({ url: 'community/locationwise/list', data: { communityId } }).then(res => {
      const result = res.data;
      if (result?.items?.length > 0) {
        const parentCity = result.items[0];
        setParentCommunityName(parentCity.Name);
        setParentCommunityId(parentCity.Id);
        setAllCommunities(parentCity.items?.length > 0 ? parentCity.items : []);
      }
    });
  };

  const handleCommunityToggle = (community: any) => {
    const isSelected = selectedCommunities.some(c => c.Id === community.Id);
    setSelectedCommunities(isSelected
      ? selectedCommunities.filter(c => c.Id !== community.Id)
      : [...selectedCommunities, community]
    );
    setSelectAllCommunities(false);
    setSelectedUsers([]);
  };

  const handleSelectAllCommunities = () => {
    if (selectAllCommunities) {
      setSelectedCommunities([]);
      setSelectAllCommunities(false);
    } else {
      setSelectedCommunities(communityOptions);
      setSelectAllCommunities(true);
    }
    setSelectedUsers([]);
  };

  const filterUsersBySelectedCommunities = () => {
    if (selectedCommunities.length === 0) { setFilteredUsers([]); return; }
    const regular = selectedCommunities.filter((c: any) => !c.isSci);
    const sci = selectedCommunities.filter((c: any) => c.isSci);
    const names = regular.map((c: any) => c.Name.toLowerCase());
    const sciUserIds = new Set<number>(sci.flatMap((c: any) => sciResidentUserIdsByCommunity[c.Id] || []));
    setFilteredUsers(allUsers.filter((u: any) =>
      (u.communityName && names.includes(u.communityName.toLowerCase())) || sciUserIds.has(u.id)
    ));
  };

  useEffect(() => { filterUsersBySelectedCommunities(); }, [selectedCommunities, allUsers, sciResidentUserIdsByCommunity]);

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleSelectAllUsers = () => {
    const ids = getFilteredUsersList().map((u: any) => u.id);
    setSelectedUsers(selectedUsers.length === ids.length && ids.length > 0 ? [] : ids);
  };

  const getFilteredUsersList = () => {
    if (!userSearchText) return filteredUsers;
    return filteredUsers.filter((u: any) =>
      u.firstName?.toLowerCase().includes(userSearchText.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearchText.toLowerCase()) ||
      u.mobile?.includes(userSearchText)
    );
  };

  // ─────────────────────────────────────────────
  // Save group (create or update)
  // ─────────────────────────────────────────────
  const handleSaveGroup = async () => {
    if (!groupName.trim()) { showSnack("Please enter a group name", "error"); return; }
    if (selectedCommunities.length === 0) { showSnack("Please select at least one community", "error"); return; }
    if (selectedUsers.length === 0) { showSnack("Please select at least one user", "error"); return; }

    setLoading(true);
    try {
      const selectedUserDetails = allUsers.filter((u: any) => selectedUsers.includes(u.id));
      const communityIds = selectedCommunities.map((c: any) => c.Id).join(',');
      const communityNames = selectedCommunities.map((c: any) => c.Name).join(', ');
      let displayCommunityName = communityNames;
      if (selectedCommunities.length > 5) {
        displayCommunityName = `${selectedCommunities.slice(0, 5).map((c: any) => c.Name).join(', ')} +${selectedCommunities.length - 5} more`;
      }

      const payload = {
        groupName: groupName,
        description: `Group with ${selectedUsers.length} users from ${selectedCommunities.length} communities`,
        communityName: displayCommunityName,
        communityIds,
        totalCommunities: selectedCommunities.length,
        communityId: parentCommunityId,
        userIds: selectedUsers.join(','),
        userDetails: JSON.stringify(selectedUserDetails),
        totalUsers: selectedUsers.length,
        status: 1,
        isActive: true
      };

      const isEditing = editingGroup !== null;
      const apiOptions = isEditing
        ? { url: `notificationgroup/update/${editingGroup.id}`, data: payload }
        : { url: 'notificationgroup/create', data: payload };

      const response = isEditing ? await http.put(apiOptions) : await http.post(apiOptions);

      if (response.data) {
        const updatedGroup = {
          id: isEditing ? editingGroup.id : (response.data.id || Date.now()),
          name: groupName,
          users: selectedUserDetails,
          communityName: displayCommunityName,
          allCommunities: selectedCommunities.map((c: any) => c.Name),
          totalCommunities: selectedCommunities.length,
          communityIds,
          createdDate: isEditing ? editingGroup.createdDate : new Date().toISOString()
        };

        setCreatedGroups(isEditing
          ? createdGroups.map((g: any) => g.id === editingGroup.id ? updatedGroup : g)
          : [...createdGroups, updatedGroup]
        );

        handleCloseGroupModal();
        showSnack(isEditing
          ? `✅ Group "${groupName}" updated successfully!`
          : `✅ Group "${groupName}" created with ${selectedUsers.length} users from ${selectedCommunities.length} communities!`
        );
      }
    } catch (error: any) {
      showSnack("❌ Failed to save group: " + (error.message || "Unknown error"), "error");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Delete group
  // ─────────────────────────────────────────────
  const handleDeleteGroup = async (groupId) => {
    setDeletingGroupId(groupId);
    try {
      await http.delete({ url: `notificationgroup/group/${groupId}`, data: {} });
      setCreatedGroups(createdGroups.filter((g: any) => g.id !== groupId));
      showSnack("✅ Group deleted successfully!");
    } catch (error: any) {
      showSnack("❌ Failed to delete group: " + (error.message || "Unknown error"), "error");
    } finally {
      setDeletingGroupId(null);
      setGroupToDelete(null);
    }
  };

  // ─────────────────────────────────────────────
  // View group modal
  // ─────────────────────────────────────────────
  const handleGroupClick = async (group) => {
    setViewGroupData({ ...group, allCommunities: [] });
    setViewGroupModalOpen(true);
    if (group.communityIds?.length > 0) {
      setLoadingGroupCommunities(true);
      const names = await fetchCommunityNamesForGroup(group.communityIds);
      setViewGroupData(prev => ({ ...prev, allCommunities: names, totalCommunities: names.length }));
      setLoadingGroupCommunities(false);
    }
  };

  // ─────────────────────────────────────────────
  // Manage members modal
  // ─────────────────────────────────────────────
  const handleOpenManageMembers = (group: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setManagingGroup(group);
    setSelectedToRemove([]);
    setSelectedToAdd([]);
    setMemberSearchText("");
    setAddSearchText("");
    setAddCommunityFilter("all");
    setNewUsersForGroup([]);
    setNewUsersChecked(false);
    setMemberTab('current');
    setManageMembersOpen(true);
    if (allUsers.length === 0) fetchAllUsers();
  };

  const handleCloseManageMembers = () => {
    setManageMembersOpen(false);
    setManagingGroup(null);
    setSelectedToRemove([]);
    setSelectedToAdd([]);
  };

  const fetchNewUsersForGroup = async (groupId: number) => {
    setLoadingNewUsers(true);
    try {
      const res = await http.get({ url: `notificationgroup/group/${groupId}/newusers` });
      const result = res.data || [];
      setNewUsersForGroup(result);
      setNewUsersChecked(true);
      if (result.length > 0) {
        const newIds = result.map((u: any) => u.id);
        setSelectedToAdd(prev => [...new Set([...prev, ...newIds])]);
      }
    } catch (err) {
      console.error("Error fetching new users:", err);
      showSnack("❌ Failed to check for new users.", "error");
    } finally {
      setLoadingNewUsers(false);
    }
  };

  const getAddTabCommunities = () => {
    const groupMemberIds = new Set((managingGroup?.users || []).map((u: any) => u.id));
    const available = allUsers.filter((u: any) => !groupMemberIds.has(u.id));
    return [...new Set(available.map((u: any) => u.communityName).filter(Boolean))].sort();
  };

  const getFilteredAddList = () => {
    const groupMemberIds = new Set((managingGroup?.users || []).map((u: any) => u.id));
    return allUsers.filter((u: any) => {
      if (groupMemberIds.has(u.id)) return false;
      if (addCommunityFilter !== "all" && u.communityName !== addCommunityFilter) return false;
      if (!addSearchText) return true;
      return (
        `${u.firstName} ${u.lastName || ''}`.toLowerCase().includes(addSearchText.toLowerCase()) ||
        u.email?.toLowerCase().includes(addSearchText.toLowerCase()) ||
        u.mobile?.includes(addSearchText)
      );
    });
  };

  const handleSaveMembers = async () => {
    if (selectedToRemove.length === 0 && selectedToAdd.length === 0) {
      handleCloseManageMembers();
      return;
    }
    setSavingMembers(true);
    try {
      let updatedUsers = [...(managingGroup?.users || [])];

      if (selectedToRemove.length > 0) {
        await http.post({
          url: `notificationgroup/group/${managingGroup.id}/removeusers`,
          data: { userIds: selectedToRemove }
        });
        updatedUsers = updatedUsers.filter((u: any) => !selectedToRemove.includes(u.id));
      }

      if (selectedToAdd.length > 0) {
        const usersToAdd = allUsers.filter((u: any) => selectedToAdd.includes(u.id));
        await http.post({
          url: `notificationgroup/group/${managingGroup.id}/addusers`,
          data: { userIds: selectedToAdd, userDetails: usersToAdd.map((u: any) => JSON.stringify(u)) }
        });
        const existingIds = new Set(updatedUsers.map((u: any) => u.id));
        updatedUsers = [...updatedUsers, ...usersToAdd.filter((u: any) => !existingIds.has(u.id))];
      }

      setCreatedGroups(createdGroups.map((g: any) =>
        g.id === managingGroup.id ? { ...g, users: updatedUsers } : g
      ));
      showSnack("✅ Group members updated successfully!");
      handleCloseManageMembers();
    } catch (error: any) {
      showSnack("❌ Failed to update members: " + (error.message || "Unknown error"), "error");
    } finally {
      setSavingMembers(false);
    }
  };

  // ─────────────────────────────────────────────
  // Export group CSV
  // ─────────────────────────────────────────────
  const exportGroupToCSV = (group) => {
    const cfg = mkConfig({ filename: `group_${group.name.replace(/\s+/g, '_')}_users`, useKeysAsHeaders: true, quoteStrings: true });
    const data = group.users.map((u: any) => ({
      name: [u.firstName, u.lastName].filter(Boolean).join(" "),
      community: u.communityName || "",
      phone: u.mobile ? '="' + u.mobile + '"' : "",
      email: u.email || ""
    }));
    download(cfg)(generateCsv(cfg)(data));
  };

  // ─────────────────────────────────────────────
  // Notification modal
  // ─────────────────────────────────────────────
  const handleOpenNotificationModal = (group) => {
    setSelectedGroupForNotification(group);
    setOpenNotificationModal(true);
    setNotificationTitle("");
    setNotificationBody("");
    setNotificationImageUrl("");
    setNotificationLink("");
  };

  const handleCloseNotificationModal = () => {
    setOpenNotificationModal(false);
    setSelectedGroupForNotification(null);
    setNotificationTitle("");
    setNotificationBody("");
    setNotificationImageUrl("");
    setNotificationLink("");
  };

  const handleSendNotification = () => {
    if (!notificationTitle.trim()) { showSnack("Please enter a notification title", "error"); return; }
    if (!notificationBody.trim()) { showSnack("Please enter a notification body", "error"); return; }
    setConfirmSendDialogOpen(true);
  };

  const sendPushNotificationToGroup = async () => {
    setSendingNotification(true);
    try {
      const response = await http.post({
        url: 'notificationgroup/sendnotification',
        data: {
          groupId: selectedGroupForNotification.id,
          title: notificationTitle.trim(),
          body: notificationBody.trim(),
          imageUrl: notificationImageUrl.trim() || null,
          link: notificationLink.trim() || null
        }
      });
      if (response.data?.success) {
        showSnack(`✅ Notification "${notificationTitle}" sent to ${selectedGroupForNotification.users.length} users!`);
        handleCloseNotificationModal();
      } else {
        showSnack(`❌ Failed to send: ${response.data?.error || 'Unknown error'}`, "error");
      }
    } catch (error: any) {
      showSnack(`❌ Failed to send: ${error.response?.data?.error || error.message || "Unknown error"}`, "error");
    } finally {
      setSendingNotification(false);
    }
  };

  // ─────────────────────────────────────────────
  // Mount
  // ─────────────────────────────────────────────
  useEffect(() => {
    initCalls();
    getUsers(currentContext, 1);
    fetchExistingGroups();
  }, []);

  useEffect(() => { setPage(0); }, [users]);
  useEffect(() => { setPage(0); }, [tagFilter]);

  const tagFilteredUsers = tagFilter === 'all'
    ? users
    : users.filter((u: any) => (u.tags || []).some((t: string) =>
        tagFilter === 'resident' ? t.startsWith('SCI Resident') : t.startsWith('SCI Merchant')));

  const paginatedUsers = tagFilteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className='user-list-ui pt-4 mt-3'>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MuiAlert elevation={6} variant="filled" severity={snackbarType} onClose={() => setSnackbarOpen(false)}>
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>

      {/* View Group Modal */}
      <Dialog open={viewGroupModalOpen} onClose={() => setViewGroupModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#f69516', color: 'white', position: 'relative' }}>
          <GroupIcon style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          Group Details
          <IconButton aria-label="close" onClick={() => setViewGroupModalOpen(false)} style={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewGroupData && (
            <>
              <Typography variant="h6" style={{ marginBottom: 8, color: '#f69516' }}>{viewGroupData.name}</Typography>
              {viewGroupData.allCommunities?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {loadingGroupCommunities ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" style={{ marginTop: '8px' }}>Loading communities...</Typography>
                    </div>
                  ) : (
                    <>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8, color: '#333' }}>
                        Communities ({viewGroupData.totalCommunities}):
                      </Typography>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', maxHeight: '400px', overflowY: 'auto', border: '1px solid #e0e0e0' }}>
                        {viewGroupData.allCommunities.map((community, idx) => (
                          <div key={idx} style={{ backgroundColor: '#fff', padding: '8px 12px', borderRadius: '4px', fontSize: '12px', border: '1px solid #f69516', display: 'flex', alignItems: 'center', gap: '6px', color: '#333', wordBreak: 'break-word' }}>
                            <span style={{ color: '#f69516', flexShrink: 0 }}>✓</span>
                            <span>{community}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ marginTop: 16, fontWeight: 'bold' }}>
                Total Users: {viewGroupData.users.length}
              </Typography>
              <div style={{ maxHeight: 350, overflowY: 'auto', marginTop: 10 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
                      <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Mobile</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewGroupData.users.map((u, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(" ")}</TableCell>
                        <TableCell>{u.email || "N/A"}</TableCell>
                        <TableCell>{u.mobile || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewGroupModalOpen(false)} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Breadcrumb + Filters */}
      <div className="title-font custom-head pl-4 pt-5">
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="primary" className="custom-link pointer breadcrumbs-style" onClick={navToBack}>
            <b>{routeData.communityName}</b>
          </Link>
          <Typography color="primary" className="breadcrumbs-style">Users</Typography>
        </Breadcrumbs>
      </div>

      <div className='row my-4 mx-2'>
        <div className="col-3">
          <FxInput name="firstName" value={currentContext.firstName} variant="outlined" label="Search" fullWidth size="small" onValueChange={handleInputChange} />
        </div>
        <div className='col-3'>
          <FxSelect name="communityId" variant="outlined" label="Community" options={communityList} selectedValue={currentContext.communityId} valueField="id" displayField="text" size="small" onValueChange={handleInputChange} />
        </div>
      </div>

      {/* Action buttons + Group cards */}
      <div className='row my-4 mx-4 pt-2'>
        <div className="col-12 mb-3">
          <button className="btn btn-success mr-3" onClick={handleOpenGroupModal} style={{ fontSize: '14px', padding: '8px 16px', backgroundColor: '#8ec73d' }}>
            <GroupIcon style={{ marginRight: '8px', fontSize: '18px', verticalAlign: 'middle' }} />
            Create Group
          </button>
          <button className="btn btn-primary" onClick={() => download(csvConfig)(generateCsv(csvConfig)(csvData))} style={{ fontSize: '14px', padding: '8px 16px' }}>
            📥 Download All Users as CSV
          </button>
        </div>

        {groupsLoading ? (
          <div className="col-12 mb-4 text-center">
            <CircularProgress size={30} />
            <Typography variant="body2" className="mt-2">Loading groups...</Typography>
          </div>
        ) : createdGroups.length > 0 ? (
          <div className="col-12 mb-4">
            <Typography variant="h6" className="mb-3" style={{ fontWeight: 'bold', color: '#333' }}>
              📂 Created Groups ({createdGroups.length})
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
              {createdGroups.map((group: any) => (
                <Card
                  key={group.id}
                  style={{ minWidth: '270px', maxWidth: '320px', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                  onClick={() => handleOpenNotificationModal(group)}
                >
                  <CardContent>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <Typography variant="h6" style={{ fontWeight: 'bold', color: '#f69516', wordBreak: 'break-word', flex: 1 }}>
                        {group.name}
                      </Typography>
                      <IconButton size="small" disabled={deletingGroupId === group.id} style={{ padding: '4px' }}
                        onClick={e => { e.stopPropagation(); setGroupToDelete(group); setDeleteDialogOpen(true); }}>
                        {deletingGroupId === group.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color="error" />}
                      </IconButton>
                    </div>

                    {group.communityName && (
                      <Chip label={group.communityName} style={{ marginBottom: '8px', backgroundColor: '#e3f2fd', color: '#f69516', border: '1px solid #f69516', maxWidth: '100%' }} />
                    )}

                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                      👥 {group.users.length} user{group.users.length !== 1 ? 's' : ''}
                    </Typography>

                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {/* <Button size="small" variant="outlined" color="primary" startIcon={<VisibilityIcon />}
                        onClick={e => { e.stopPropagation(); handleGroupClick(group); }} style={{ fontSize: '11px' }}>
                        View
                      </Button> */}
                      <Button size="small" variant="outlined" startIcon={<EditIcon />}
                        onClick={e => handleEditGroup(group, e)}
                        style={{ fontSize: '11px', color: '#f69516', borderColor: '#f69516' }}>
                        Edit
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<PeopleIcon />}
                        onClick={e => handleOpenManageMembers(group, e)}
                        style={{ fontSize: '11px', color: '#8ec73d', borderColor: '#8ec73d' }}>
                        Members
                      </Button>
                      <Button size="small" variant="outlined" color="secondary"
                        onClick={e => { e.stopPropagation(); exportGroupToCSV(group); }} style={{ fontSize: '11px' }}>
                        Export
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="col-12 mb-4">
            <Paper style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>
              <Typography variant="body2" color="textSecondary">
                📭 No groups created yet. Click "Create Group" to get started.
              </Typography>
            </Paper>
          </div>
        )}

        {/* Users table */}
        <div className="col-12">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Chip
                label={`All (${users.length})`}
                size="small"
                onClick={() => setTagFilter('all')}
                style={{
                  backgroundColor: tagFilter === 'all' ? '#f69516' : '#eee',
                  color: tagFilter === 'all' ? '#fff' : '#333',
                  cursor: 'pointer'
                }}
              />
              <Chip
                label={`SCI Residents (${users.filter((u: any) => (u.tags || []).some((t: string) => t.startsWith('SCI Resident'))).length})`}
                size="small"
                onClick={() => setTagFilter('resident')}
                style={{
                  backgroundColor: tagFilter === 'resident' ? '#1565c0' : '#e3f2fd',
                  color: tagFilter === 'resident' ? '#fff' : '#1565c0',
                  cursor: 'pointer'
                }}
              />
              <Chip
                label={`SCI Merchants (${users.filter((u: any) => (u.tags || []).some((t: string) => t.startsWith('SCI Merchant'))).length})`}
                size="small"
                onClick={() => setTagFilter('merchant')}
                style={{
                  backgroundColor: tagFilter === 'merchant' ? '#e65100' : '#fff3e0',
                  color: tagFilter === 'merchant' ? '#fff' : '#e65100',
                  cursor: 'pointer'
                }}
              />
            </div>
            <TablePagination
              count={tagFilteredUsers.length}
              page={page}
              onPageChange={(e, newPage) => { setPage(newPage); if (tagFilter === 'all') getUsers(currentContext, newPage + 1); }}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[50]}
              onRowsPerPageChange={() => {}}
              labelDisplayedRows={({ from, to, count }) => `Showing ${from}-${to} of ${count}`}
            />
          </div>
          <TableContainer component={Paper}>
            <Table className="table-striped" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created Date & Time</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Community</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tagFilteredUsers.length === 0 && <TableRow><TableCell colSpan={7}><NoRecords /></TableCell></TableRow>}
                {tagFilteredUsers.length > 0 && paginatedUsers.map((row: any, i) => (
                  <TableRow key={page * rowsPerPage + i}>
                    <TableCell>
                      {row.user.createdDate ? (() => {
                        const [date, time] = row.user.createdDate.split(/[ T]/);
                        const formattedTime = time ? new Date(`${date}T${time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                        return <><span>{date}</span><br /><span>{formattedTime}</span></>;
                      })() : ""}
                    </TableCell>
                    <TableCell>
                      <span>{row.user.firstName}</span>
                      {row.user.lastName && <span> {row.user.lastName}</span>}
                    </TableCell>
                    <TableCell>{row.user.communityName}</TableCell>
                    <TableCell>{row.user.mobile}</TableCell>
                    <TableCell>{row.user.email}</TableCell>
                    <TableCell>{row.user.referralCode}</TableCell>
                    <TableCell>
                      {(row.tags || []).map((tag: string, ti: number) => (
                        <Chip key={ti} size="small" label={tag}
                          style={{ marginRight: '4px', marginBottom: '2px', backgroundColor: tag.startsWith('SCI Resident') ? '#e3f2fd' : '#fff3e0', color: tag.startsWith('SCI Resident') ? '#1565c0' : '#e65100' }} />
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Create / Edit Group Modal */}
      <Dialog open={openGroupModal} onClose={handleCloseGroupModal} maxWidth="lg" fullWidth>
        <DialogTitle style={{ backgroundColor: '#f69516', color: 'white', position: 'relative' }}>
          {editingGroup
            ? <EditIcon style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            : <GroupIcon style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          }
          {editingGroup ? 'Edit Group' : 'Create New Group'}
          {editingGroup && (
            <span style={{ marginLeft: '10px', fontSize: '12px', backgroundColor: '#fff3e0', color: '#e65100', padding: '2px 8px', borderRadius: '4px' }}>
              editing
            </span>
          )}
          <IconButton aria-label="close" onClick={handleCloseGroupModal} style={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '500px', paddingTop: '20px' }}>
          <div className="mb-4">
            <FxInput name="groupName" value={groupName} variant="outlined" label="Group Name *" fullWidth size="small"
              onValueChange={(item) => setGroupName(item.value)} placeholder="Enter a descriptive group name" />
          </div>

          {parentCommunityName && (
            <Paper style={{ padding: '12px 16px', marginBottom: '16px', backgroundColor: '#e3f2fd', border: '1px solid #f69516', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
              <InfoIcon style={{ color: '#f69516', marginRight: '12px' }} />
              <Typography variant="body2" style={{ color: '#f69516' }}>
                Showing communities under <strong>{parentCommunityName}</strong>
              </Typography>
            </Paper>
          )}

          {communitiesLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={30} />
              <Typography variant="body2" style={{ marginTop: '8px' }}>Loading communities...</Typography>
            </div>
          ) : communityOptions.length > 0 ? (
            <div className="mb-4">
              <Paper style={{ padding: '16px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <FormControlLabel
                    control={<Checkbox checked={selectAllCommunities} onChange={handleSelectAllCommunities} color="primary" indeterminate={selectedCommunities.length > 0 && selectedCommunities.length < communityOptions.length} />}
                    label={<Typography style={{ fontWeight: 'bold', color: '#f69516' }}>Select All Communities ({selectedCommunities.length}/{communityOptions.length})</Typography>}
                  />
                </div>
                <FormGroup style={{ paddingLeft: '20px', maxHeight: '250px', overflowY: 'auto' }}>
                  {communityOptions.map((community: any, idx) => (
                    <FormControlLabel key={idx}
                      control={<Checkbox checked={selectedCommunities.some(c => c.Id === community.Id)} onChange={() => handleCommunityToggle(community)} color="primary" />}
                      label={
                        <Typography variant="body2">
                          {community.Name}
                          {community.isSci && (
                            <Chip label="SCI" size="small" style={{ marginLeft: '6px', height: '18px', fontSize: '10px', backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                          )}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
              </Paper>

              {selectedCommunities.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                    Selected Communities: {selectedCommunities.length}
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                    {selectedCommunities.map((community: any, idx) => (
                      <Chip key={idx} label={community.Name} onDelete={() => handleCommunityToggle(community)} color="primary" variant="outlined" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            !communitiesLoading && (
              <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '16px' }}>
                <Typography variant="body1" color="textSecondary">
                  ⚠️ No communities found {parentCommunityName ? `under "${parentCommunityName}"` : ''}
                </Typography>
              </div>
            )
          )}

          {filteredUsers.length > 0 && (
            <>
              <div className="mb-3">
                <FxInput name="userSearch" value={userSearchText} variant="outlined" label="Search Users (Name, Email, Phone)" fullWidth size="small"
                  onValueChange={(item) => setUserSearchText(item.value)} placeholder="Type to filter users..." />
              </div>
              <div className="mb-3">
                <FormControlLabel
                  control={<Checkbox checked={selectedUsers.length === getFilteredUsersList().length && getFilteredUsersList().length > 0} indeterminate={selectedUsers.length > 0 && selectedUsers.length < getFilteredUsersList().length} onChange={handleSelectAllUsers} color="primary" />}
                  label={<Typography style={{ fontWeight: 'bold' }}>Select All Users ({selectedUsers.length} of {getFilteredUsersList().length} selected)</Typography>}
                />
              </div>
              <Paper style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" style={{ backgroundColor: '#f5f5f5' }} />
                      <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Community</TableCell>
                      <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Mobile</TableCell>
                      <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredUsersList().length > 0 ? getFilteredUsersList().map((user: any) => (
                      <TableRow key={user.id} hover onClick={() => handleUserSelect(user.id)}
                        style={{ cursor: 'pointer', backgroundColor: selectedUsers.includes(user.id) ? '#e3f2fd' : 'transparent' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedUsers.includes(user.id)} onChange={() => handleUserSelect(user.id)} color="primary" onClick={e => e.stopPropagation()} />
                        </TableCell>
                        <TableCell style={{ fontWeight: selectedUsers.includes(user.id) ? 'bold' : 'normal' }}>{user.firstName} {user.lastName || ''}</TableCell>
                        <TableCell>{user.communityName || 'N/A'}</TableCell>
                        <TableCell>{user.mobile || 'N/A'}</TableCell>
                        <TableCell>{user.email || 'N/A'}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="textSecondary" style={{ padding: '20px' }}>
                            {userSearchText ? 'No users found matching your search' : 'No users available'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}

          {usersLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <CircularProgress size={40} />
              <Typography variant="body1" style={{ marginTop: '16px' }}>Loading users...</Typography>
            </div>
          )}

          {selectedCommunities.length > 0 && filteredUsers.length === 0 && !usersLoading && (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
              <Typography variant="body1" color="textSecondary">⚠️ No users found in the selected communities</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', backgroundColor: '#f5f5f5' }}>
          <Button onClick={handleCloseGroupModal} color="default" variant="outlined">Cancel</Button>
          <Button
            onClick={handleSaveGroup}
            color="primary"
            variant="contained"
            disabled={!groupName.trim() || selectedCommunities.length === 0 || selectedUsers.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : editingGroup ? <EditIcon /> : <GroupIcon />}
          >
            {loading
              ? (editingGroup ? 'Updating...' : 'Creating...')
              : editingGroup
                ? `Update Group (${selectedUsers.length} users, ${selectedCommunities.length} communities)`
                : `Create Group (${selectedUsers.length} users from ${selectedCommunities.length} communities)`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Modal */}
      <Dialog open={manageMembersOpen} onClose={handleCloseManageMembers} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#f69516', color: 'white', position: 'relative' }}>
          <PeopleIcon style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          Manage Members — {managingGroup?.name}
          <IconButton aria-label="close" onClick={handleCloseManageMembers} style={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
            {(['current', 'add'] as const).map(tab => (
              <button key={tab} onClick={() => setMemberTab(tab)} style={{ flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontWeight: memberTab === tab ? 600 : 400, borderBottom: memberTab === tab ? '2px solid #f69516' : '2px solid transparent', background: 'white', fontSize: '13px', color: memberTab === tab ? '#f69516' : '#666' }}>
                {tab === 'current'
                  ? `Current Members (${managingGroup?.users?.length ?? 0}${selectedToRemove.length > 0 ? ` — ${selectedToRemove.length} to remove` : ''})`
                  : `Add Members (${selectedToAdd.length} selected)`}
              </button>
            ))}
          </div>

          <div style={{ padding: '16px', minHeight: '400px' }}>

            {/* Current members tab */}
            {memberTab === 'current' && managingGroup && (
              <>
                {selectedToRemove.length > 0 && (
                  <Paper style={{ padding: '8px 12px', marginBottom: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                    <Typography variant="body2" style={{ color: '#856404' }}>
                      ⚠️ {selectedToRemove.length} user{selectedToRemove.length > 1 ? 's' : ''} marked for removal. Click "Save Changes" to apply.
                    </Typography>
                  </Paper>
                )}
                <TextField
                  placeholder="Search current members..."
                  variant="outlined" size="small" fullWidth value={memberSearchText}
                  onChange={e => setMemberSearchText(e.target.value)}
                  style={{ marginBottom: '12px' }}
                />
                <Paper style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" style={{ backgroundColor: '#f5f5f5' }} />
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Community</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Mobile</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(managingGroup.users || [])
                        .filter((u: any) => !memberSearchText ||
                          `${u.firstName} ${u.lastName || ''}`.toLowerCase().includes(memberSearchText.toLowerCase()) ||
                          u.email?.toLowerCase().includes(memberSearchText.toLowerCase()) ||
                          u.mobile?.includes(memberSearchText)
                        )
                        .map((u: any) => {
                          const marked = selectedToRemove.includes(u.id);
                          return (
                            <TableRow key={u.id} hover
                              onClick={() => setSelectedToRemove(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                              style={{ cursor: 'pointer', backgroundColor: marked ? '#ffebee' : 'transparent', opacity: marked ? 0.7 : 1 }}>
                              <TableCell padding="checkbox">
                                <Checkbox checked={marked} color="secondary" onClick={e => e.stopPropagation()}
                                  onChange={() => setSelectedToRemove(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} />
                              </TableCell>
                              <TableCell style={{ textDecoration: marked ? 'line-through' : 'none', color: marked ? '#c62828' : 'inherit' }}>
                                {u.firstName} {u.lastName || ''}
                              </TableCell>
                              <TableCell>{u.communityName || 'N/A'}</TableCell>
                              <TableCell>{u.mobile || 'N/A'}</TableCell>
                              <TableCell>{u.email || 'N/A'}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Paper>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                  Check a user to mark them for removal, then click Save Changes.
                </Typography>
              </>
            )}

            {/* Add members tab */}
            {memberTab === 'add' && (
              <>
                {/* Sync new users banner */}
                <Paper style={{ padding: '12px 16px', marginBottom: '16px', backgroundColor: newUsersChecked && newUsersForGroup.length === 0 ? '#e8f5e9' : '#e3f2fd', border: `1px solid ${newUsersChecked && newUsersForGroup.length === 0 ? '#81c784' : '#90caf9'}`, borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#1565c0' }}>🔄 Sync new community members</Typography>
                      <Typography variant="caption" style={{ color: '#1976d2' }}>
                        {!newUsersChecked
                          ? `Find users who joined this group's communities after ${new Date(managingGroup?.createdDate).toLocaleDateString()} and aren't in the group yet.`
                          : newUsersForGroup.length > 0
                            ? `Found ${newUsersForGroup.length} new user${newUsersForGroup.length > 1 ? 's' : ''} — auto-selected below.`
                            : `All community members are already in this group.`
                        }
                      </Typography>
                    </div>
                    {!newUsersChecked ? (
                      <Button size="small" variant="contained" onClick={() => fetchNewUsersForGroup(managingGroup?.id)} disabled={loadingNewUsers}
                        style={{ backgroundColor: '#1976d2', color: 'white', whiteSpace: 'nowrap', marginLeft: '12px' }}
                        startIcon={loadingNewUsers ? <CircularProgress size={14} color="inherit" /> : null}>
                        {loadingNewUsers ? 'Checking...' : 'Check for new users'}
                      </Button>
                    ) : newUsersForGroup.length > 0 ? (
                      <Chip label={`${newUsersForGroup.length} new — auto-selected`} style={{ backgroundColor: '#bbdefb', color: '#0d47a1', marginLeft: '12px' }} />
                    ) : (
                      <Chip label="Up to date" style={{ backgroundColor: '#c8e6c9', color: '#1b5e20', marginLeft: '12px' }} />
                    )}
                  </div>
                  {newUsersChecked && newUsersForGroup.length > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {newUsersForGroup.slice(0, 10).map((u: any) => (
                        <Chip key={u.id} size="small" label={`${u.firstName} ${u.lastName || ''} (${u.communityName || 'N/A'})`} style={{ fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                      ))}
                      {newUsersForGroup.length > 10 && (
                        <Chip size="small" label={`+${newUsersForGroup.length - 10} more`} style={{ fontSize: '11px', backgroundColor: '#bbdefb', color: '#0d47a1' }} />
                      )}
                    </div>
                  )}
                </Paper>

                {/* Selected count banner */}
                {selectedToAdd.length > 0 && (
                  <Paper style={{ padding: '8px 12px', marginBottom: '12px', backgroundColor: '#e8f5e9', border: '1px solid #81c784', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" style={{ color: '#2e7d32' }}>
                      ✅ {selectedToAdd.length} user{selectedToAdd.length > 1 ? 's' : ''} selected to add
                    </Typography>
                    <Button size="small" onClick={() => setSelectedToAdd([])} style={{ color: '#c62828', fontSize: '11px' }}>Clear all</Button>
                  </Paper>
                )}

                {/* Search + community filter */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <TextField
                    placeholder="Search by name, email, phone..."
                    variant="outlined" size="small" value={addSearchText}
                    onChange={e => setAddSearchText(e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <TextField
                    select label="Community" variant="outlined" size="small"
                    value={addCommunityFilter}
                    onChange={e => setAddCommunityFilter(e.target.value)}
                    style={{ flex: 1, minWidth: '160px' }}
                    SelectProps={{ native: true }}
                  >
                    <option value="all">All communities</option>
                    {getAddTabCommunities().map((name: string) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </TextField>
                </div>

                {/* Users table */}
                <Paper style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" style={{ backgroundColor: '#f5f5f5' }}>
                          <Checkbox color="primary"
                            indeterminate={(() => {
                              const v = getFilteredAddList();
                              const s = v.filter((u: any) => selectedToAdd.includes(u.id));
                              return s.length > 0 && s.length < v.length;
                            })()}
                            checked={(() => {
                              const v = getFilteredAddList();
                              return v.length > 0 && v.every((u: any) => selectedToAdd.includes(u.id));
                            })()}
                            onChange={() => {
                              const v = getFilteredAddList();
                              const allSel = v.every((u: any) => selectedToAdd.includes(u.id));
                              if (allSel) {
                                const vIds = new Set(v.map((u: any) => u.id));
                                setSelectedToAdd(prev => prev.filter(id => !vIds.has(id)));
                              } else {
                                setSelectedToAdd(prev => [...new Set([...prev, ...v.map((u: any) => u.id)])]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Community</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Mobile</TableCell>
                        <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>New?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usersLoading ? (
                        <TableRow><TableCell colSpan={5} align="center" style={{ padding: '40px' }}><CircularProgress size={30} /></TableCell></TableRow>
                      ) : getFilteredAddList().length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="textSecondary" style={{ padding: '20px' }}>No users found matching your filters</Typography>
                        </TableCell></TableRow>
                      ) : getFilteredAddList().map((u: any) => {
                        const selected = selectedToAdd.includes(u.id);
                        const isNew = newUsersForGroup.some((n: any) => n.id === u.id);
                        return (
                          <TableRow key={u.id} hover
                            onClick={() => setSelectedToAdd(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                            style={{ cursor: 'pointer', backgroundColor: selected ? (isNew ? '#e8f5e9' : '#e3f2fd') : (isNew ? '#f1f8e9' : 'transparent') }}>
                            <TableCell padding="checkbox">
                              <Checkbox checked={selected} color="primary" onClick={e => e.stopPropagation()}
                                onChange={() => setSelectedToAdd(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} />
                            </TableCell>
                            <TableCell style={{ fontWeight: selected ? 600 : 400 }}>{u.firstName} {u.lastName || ''}</TableCell>
                            <TableCell>{u.communityName || 'N/A'}</TableCell>
                            <TableCell>{u.mobile || 'N/A'}</TableCell>
                            <TableCell>
                              {isNew && <Chip size="small" label="New" style={{ fontSize: '10px', height: '18px', backgroundColor: '#c8e6c9', color: '#1b5e20' }} />}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                  Users already in the group are hidden. Green rows = new since group was created.
                </Typography>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" color="textSecondary" style={{ flex: 1 }}>
            {selectedToRemove.length > 0 && `${selectedToRemove.length} to remove`}
            {selectedToRemove.length > 0 && selectedToAdd.length > 0 && ' · '}
            {selectedToAdd.length > 0 && `${selectedToAdd.length} to add`}
          </Typography>
          <Button onClick={handleCloseManageMembers} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSaveMembers}
            variant="contained"
            disabled={savingMembers || (selectedToRemove.length === 0 && selectedToAdd.length === 0)}
            style={{
              background: (selectedToRemove.length > 0 || selectedToAdd.length > 0) ? '#f69516' : undefined,
              color: (selectedToRemove.length > 0 || selectedToAdd.length > 0) ? 'white' : undefined
            }}
            startIcon={savingMembers ? <CircularProgress size={16} color="inherit" /> : <PeopleIcon />}
          >
            {savingMembers ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Modal */}
      <Dialog open={openNotificationModal} onClose={handleCloseNotificationModal} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#f69516', color: 'white', position: 'relative' }}>
          <NotificationsActiveIcon style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          Send Push Notification
          <IconButton aria-label="close" onClick={handleCloseNotificationModal} style={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ paddingTop: '20px' }}>
          {selectedGroupForNotification && (
            <>
              <Paper style={{ padding: '12px 16px', marginBottom: '20px', backgroundColor: '#e8f5e9', border: '1px solid #8ec73d', borderRadius: '4px' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#2e7d32' }}>Group: {selectedGroupForNotification.name}</Typography>
                <Typography variant="caption" style={{ color: '#388e3c' }}>Recipients: {selectedGroupForNotification.users.length} users</Typography>
              </Paper>
              <div className="mb-4">
                <TextField label="Notification Title *" variant="outlined" fullWidth value={notificationTitle} onChange={e => setNotificationTitle(e.target.value)} placeholder="Enter notification title" size="small" />
              </div>
              <div className="mb-4">
                <TextField label="Notification Body *" variant="outlined" fullWidth multiline rows={4} value={notificationBody} onChange={e => setNotificationBody(e.target.value)} placeholder="Enter notification message" size="small" />
              </div>
              <div className="mb-4">
                <TextField label="Image URL (Optional)" variant="outlined" fullWidth value={notificationImageUrl} onChange={e => setNotificationImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" size="small" />
              </div>
              <div className="mb-4">
                <TextField label="Link/Action URL (Optional)" variant="outlined" fullWidth value={notificationLink} onChange={e => setNotificationLink(e.target.value)} placeholder="https://example.com/action" size="small" />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', backgroundColor: '#f5f5f5' }}>
          <Button onClick={handleCloseNotificationModal} color="default" variant="outlined">Cancel</Button>
          <Button
            onClick={handleSendNotification}
            disabled={!notificationTitle.trim() || !notificationBody.trim() || sendingNotification}
            style={{
              background: (!notificationTitle.trim() || !notificationBody.trim() || sendingNotification) ? '#fff' : '#8ec73d',
              color: (!notificationTitle.trim() || !notificationBody.trim() || sendingNotification) ? '#222' : '#fff',
              border: '1.5px solid #bbbbbb',
              fontWeight: 600,
              boxShadow: 'none'
            }}
            variant="contained"
          >
            {sendingNotification ? 'SENDING...' : 'SEND NOTIFICATION'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Group?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <b>{groupToDelete?.name || ""}</b>? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="default" variant="outlined">Cancel</Button>
          <Button
            onClick={async () => { setDeleteDialogOpen(false); if (groupToDelete) await handleDeleteGroup(groupToDelete.id); }}
            color="secondary" variant="contained" disabled={deletingGroupId === groupToDelete?.id}
          >
            {deletingGroupId === groupToDelete?.id ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Send Notification Dialog */}
      <Dialog open={confirmSendDialogOpen} onClose={() => setConfirmSendDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Push Notification?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send push notifications to all {selectedGroupForNotification?.users.length || 0} members of "{selectedGroupForNotification?.name}" group?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendDialogOpen(false)} color="default" variant="outlined">Cancel</Button>
          <Button onClick={() => { setConfirmSendDialogOpen(false); sendPushNotificationToGroup(); }} color="primary" variant="contained">Send</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default UserList;